import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { X, PaperPlaneTilt, Plus } from "@phosphor-icons/react";

export function SendEmailDialog({ open, onOpenChange, sessionId, API, filters, sortConfig }) {
  const [users, setUsers] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [extraEmail, setExtraEmail] = useState("");
  const [extraEmails, setExtraEmails] = useState([]);
  const [subject, setSubject] = useState("");
  const [preText, setPreText] = useState("");
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [smtpConfigured, setSmtpConfigured] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMessage(null);
    setSending(false);

    // Build dynamic subject based on active filters
    const baseSubject = "TVB SR Lizenzen";
    const filterParts = [];
    if (filters.geschlecht !== "ALLE") filterParts.push(filters.geschlecht);
    if (filters.spieler !== "ALLE") filterParts.push(filters.spieler === "OHNE" ? "Ohne Spieler-Lizenz" : filters.spieler);
    if (filters.mannschaft !== "ALLE") filterParts.push(filters.mannschaft === "OHNE" ? "Ohne Mannschaft" : filters.mannschaft);
    if (filters.sr !== "ALLE") filterParts.push(filters.sr === "OHNE" ? "Ohne SR-Lizenz" : filters.sr);
    if (filters.gueltigBis !== "ALLE") filterParts.push(filters.gueltigBis);
    setSubject(filterParts.length > 0 ? `${baseSubject}: ${filterParts.join(" ")}` : baseSubject);

    axios.get(`${API}/users/emails?session_id=${sessionId}`).then((res) => {
      // Exclude admin users from recipient list
      const nonAdminUsers = (res.data.users || []).filter(u => u.role !== "admin");
      setUsers(nonAdminUsers);
    }).catch(() => {});

    axios.get(`${API}/smtp-config/status?session_id=${sessionId}`).then((res) => {
      setSmtpConfigured(res.data.configured || false);
    }).catch(() => setSmtpConfigured(false));
  }, [open, sessionId, API, filters]);

  const toggleUser = (email) => {
    setSelectedEmails((prev) => prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]);
  };

  const addExtraEmail = () => {
    const trimmed = extraEmail.trim();
    if (trimmed && trimmed.includes("@") && !extraEmails.includes(trimmed) && !selectedEmails.includes(trimmed)) {
      setExtraEmails((prev) => [...prev, trimmed]);
      setExtraEmail("");
    }
  };

  const removeExtraEmail = (email) => {
    setExtraEmails((prev) => prev.filter((e) => e !== email));
  };

  const allRecipients = [...selectedEmails, ...extraEmails];

  const handleSend = async () => {
    // Warn if extra email field has text but wasn't added via PLUS
    if (extraEmail.trim()) {
      const proceed = window.confirm(`"${extraEmail.trim()}" wurde nicht als Empfänger hinzugefügt. Trotzdem senden?`);
      if (!proceed) return;
    }
    if (allRecipients.length === 0) {
      setMessage({ type: "error", text: "Mindestens ein Empfänger erforderlich" });
      return;
    }
    if (!subject.trim()) {
      setMessage({ type: "error", text: "Bitte einen Betreff eingeben" });
      return;
    }
    setSending(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/send-email?session_id=${sessionId}`, {
        recipients: allRecipients,
        subject: subject.trim(),
        pre_text: preText,
        filter_geschlecht: filters.geschlecht,
        filter_spieler: filters.spieler,
        filter_sr: filters.sr,
        filter_gueltig_bis: filters.gueltigBis,
        filter_mannschaft: filters.mannschaft,
        sort_key: sortConfig.key,
        sort_direction: sortConfig.direction,
      });
      setMessage({ type: "success", text: res.data.message });
      setTimeout(() => onOpenChange(false), 2000);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Fehler beim Senden" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaperPlaneTilt className="h-5 w-5" />
            Tabelle per E-Mail senden
          </DialogTitle>
        </DialogHeader>

        {smtpConfigured === false ? (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded">
            SMTP ist nicht konfiguriert. Bitte den Administrator kontaktieren.
          </div>
        ) : (
          <div className="space-y-3 overflow-y-auto flex-1 min-h-0 px-1 -mx-1 pr-2">
            {/* Recipients */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Empfänger</label>
              <div className="mt-1 space-y-1 max-h-32 overflow-y-auto border border-border rounded p-2">
                {users.map((u) => (
                  <label key={u.username} className={`flex items-center gap-2 text-xs rounded px-1 py-0.5 ${u.email ? 'cursor-pointer hover:bg-secondary' : 'opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={selectedEmails.includes(u.email)}
                      onChange={() => u.email && toggleUser(u.email)}
                      disabled={!u.email}
                      className="rounded"
                      data-testid={`recipient-${u.username}`}
                    />
                    <span className="font-medium">{u.username}</span>
                    {u.email ? (
                      <span className="text-muted-foreground">({u.email})</span>
                    ) : (
                      <span className="text-destructive italic">(keine E-Mail)</span>
                    )}
                  </label>
                ))}
                {users.length === 0 && <p className="text-xs text-muted-foreground">Keine Benutzer mit E-Mail gefunden</p>}
              </div>
            </div>

            {/* Extra emails */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Weitere Empfänger</label>
              <div className="flex gap-1 mt-1">
                <Input
                  data-testid="extra-email-input"
                  value={extraEmail}
                  onChange={(e) => setExtraEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="text-xs h-7"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtraEmail(); } }}
                />
                <Button data-testid="add-extra-email-btn" variant="outline" size="sm" className="h-7 px-2" onClick={addExtraEmail}>
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
              {extraEmails.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {extraEmails.map((email) => (
                    <span key={email} className="inline-flex items-center gap-1 text-[10px] bg-secondary px-2 py-0.5 rounded">
                      {email}
                      <button onClick={() => removeExtraEmail(email)} className="hover:text-destructive"><X className="h-2.5 w-2.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Subject */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Betreff</label>
              <Input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="text-xs h-7 mt-1" />
            </div>

            {/* Pre-text */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Text vor der Tabelle</label>
              <textarea
                data-testid="email-pretext"
                value={preText}
                onChange={(e) => setPreText(e.target.value)}
                className="w-full mt-1 text-xs border border-border rounded bg-background p-2 min-h-[60px] max-h-[120px] resize-y focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Optionaler Text, der vor der Tabelle erscheint..."
              />
            </div>

            {/* Active filters info */}
            {(filters.geschlecht !== "ALLE" || filters.spieler !== "ALLE" || filters.sr !== "ALLE" || filters.gueltigBis !== "ALLE" || filters.mannschaft !== "ALLE") && (
              <div className="text-[10px] text-amber-600 dark:text-amber-400 bg-amber-500/10 p-2 rounded">
                Aktive Filter werden angewendet. Die E-Mail enthält nur die gefilterten Daten.
              </div>
            )}

            {message && (
              <div data-testid="email-message" className={`text-xs p-2 rounded ${message.type === "success" ? "bg-green-500/10 text-green-700 dark:text-green-400" : "bg-destructive/10 text-destructive"}`}>
                {message.text}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border mt-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="text-xs">Abbrechen</Button>
          {smtpConfigured !== false && (
            <Button data-testid="send-email-btn" size="sm" onClick={handleSend} disabled={sending || allRecipients.length === 0} className="text-xs">
              {sending ? (
                <><div className="animate-spin h-3 w-3 border-2 border-primary-foreground border-t-transparent rounded-full mr-1" /> Sende...</>
              ) : (
                <><PaperPlaneTilt className="h-3 w-3 mr-1" /> E-Mail senden</>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
